import { ref, computed } from 'vue'
import type { Ref } from 'vue'

import { IbexaImageVariations } from '@/enums/IbexaImageVariations'
import { useContentApi } from '@/composables/useCmsContentApi'
import { useQuery } from '@tanstack/vue-query'

import type { IbexaField, IbexaImageField, IbexaRichTextField } from '@/types/Ibexa'
import type { Image } from '@/types/Image'
import { findField } from '@/utils'

type ImageOptions = {
  imageId?: string,
  svgId?: string,
  imageVariation?: IbexaImageVariations
}

export const useImage = (options?: ImageOptions) => {
  const { getContentFields } = useContentApi()

  const imageId = ref<string>(options?.imageId ?? '')
  const svgId = ref<string>(options?.svgId ?? '')
  const imageVariation = ref<IbexaImageVariations>(IbexaImageVariations.ThumbnailLarge)

  const setImageOptions = (options: ImageOptions) => {
    if (options.imageId) imageId.value = options.imageId
    if (options.svgId) svgId.value = options.svgId
    if (options.imageVariation) imageVariation.value = options.imageVariation
  }

  const mapSvgFields = (fields: IbexaField[], id: string): Image => ({
    altText: findField(fields, 'alt_text') as string,
    caption: findField(fields, 'caption') as IbexaRichTextField,
    copyright: findField(fields, 'copyright') as string,
    src: `${import.meta.env.VITE_BASE_API_URL}/image/svg/${id}/svg_file/svg_file.svg`,
  })

  const mapImageFields = (fields: IbexaField[], id: string, variation: IbexaImageVariations = IbexaImageVariations.ThumbnailLarge): Image => ({
    altText: (findField(fields, 'image') as IbexaImageField).alternativeText || '',
    caption: findField(fields, 'caption') as IbexaRichTextField,
    copyright: findField(fields, 'copyright') as string || '',
    src: getImageByField(id, 'image', variation),
  })

  const getImageByField = (
    contentId: number | string,
    fieldDefinitionId: string = 'image',
    variation: IbexaImageVariations = IbexaImageVariations.ThumbnailLarge,
  ) => `${import.meta.env.VITE_BASE_API_URL}/bilde/${contentId}/${fieldDefinitionId}/${variation}`

  const createImageQuery = (id: Ref<string>, mapFields: (fields: IbexaField[], id: string) => Image) => {
    return useQuery<Image | null>({
      queryKey: ['img', id],
      queryFn: async () => {
        const fields = await getContentFields(id.value)
        return mapFields(fields, id.value)
      },
      enabled: computed(() => !!id.value),
      staleTime: Infinity
    })
  }

  const svgQuery = createImageQuery(svgId, mapSvgFields)
  const imageQuery = createImageQuery(imageId, (fields) => mapImageFields(fields, imageId.value, imageVariation.value))

  const src = computed(() => imageQuery.data?.value?.src || svgQuery.data?.value?.src || '')
  const altText = computed(() => imageQuery.data?.value?.altText || svgQuery.data?.value?.altText || '')
  const caption = computed(() => imageQuery.data?.value?.caption || svgQuery.data?.value?.caption || { xhtml5output: '' })
  const copyright = computed(() => imageQuery.data?.value?.copyright || svgQuery.data?.value?.copyright || '')
  const isLoading = computed(() => imageQuery?.isLoading?.value || svgQuery.isLoading?.value)

  return {
    src,
    altText,
    caption,
    copyright,
    isLoading,
    getImageByField,
    setImageOptions,
  }
}
